import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Award,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  AlertOctagon
} from 'lucide-react';

/**
 * Driver Risk Badge Component
 * Displays a color-coded badge based on driver risk category
 *
 * Risk score is an insurance-style index centred on 100 (the neutral baseline:
 * a driver with no claims and no data scores exactly 100). Lower = better.
 * Claim malus = count×5 + at-fault×10, so 1 at-fault claim ≈ 115, 2 ≈ 130.
 *
 * Risk Categories (calibrated so the neutral 100 is 'average', a single
 * at-fault claim tips into 'elevated', two into 'high'):
 * - excellent: score < 70 (strong bonuses - best drivers)
 * - good: 70-84 (below average - good drivers)
 * - average: 85-109 (neutral baseline ~100 - normal risk)
 * - elevated: 110-124 (≈1 at-fault claim - needs attention)
 * - high: >= 125 (≈2+ at-fault claims - requires action)
 */

// `label`/`description` = repli FR — l'affichage passe par t('drivers.risk.categories.<code>.*')
const RISK_CONFIG = {
  excellent: {
    bg: 'bg-emerald-100',
    text: 'text-emerald-800',
    border: 'border-emerald-300',
    icon: Award,
    label: 'Excellent',
    description: 'Conducteur exemplaire',
  },
  good: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    icon: CheckCircle,
    label: 'Bon',
    description: 'Risque faible',
  },
  low: {
    bg: 'bg-green-100',
    text: 'text-green-800',
    border: 'border-green-300',
    icon: CheckCircle,
    label: 'Faible',
    description: 'Risque faible',
  },
  average: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800',
    border: 'border-yellow-300',
    icon: AlertCircle,
    label: 'Moyen',
    description: 'Risque normal',
  },
  elevated: {
    bg: 'bg-orange-100',
    text: 'text-orange-800',
    border: 'border-orange-300',
    icon: AlertTriangle,
    label: 'Élevé',
    description: 'Attention requise',
  },
  high: {
    bg: 'bg-red-100',
    text: 'text-red-800',
    border: 'border-red-300',
    icon: AlertOctagon,
    label: 'Très élevé',
    description: 'Action requise',
  },
};

/**
 * Calculate risk category from score
 * Lower score = better (less risky)
 */
function getRiskCategory(score) {
  if (score < 70) return 'excellent';
  if (score < 85) return 'good';
  if (score < 110) return 'average';   // 85-109 — neutral baseline ~100
  if (score < 125) return 'elevated';  // 110-124 — ≈1 at-fault claim
  return 'high';                       // >=125 — ≈2+ at-fault claims
}

/**
 * DriverRiskBadge - Compact badge for tables and lists
 */
function DriverRiskBadge({
  riskCategory,
  riskScore,
  size = 'md',
  showScore = false,
  showIcon = true,
  className = ''
}) {
  const { t } = useTranslation();
  // Use provided category or calculate from score
  const category = riskCategory || getRiskCategory(riskScore || 100);
  const config = RISK_CONFIG[category] || RISK_CONFIG.average;
  const Icon = config.icon;
  const code = RISK_CONFIG[category] ? category : 'average';
  const label = t(`drivers.risk.categories.${code}.label`, { defaultValue: config.label });
  const description = t(`drivers.risk.categories.${code}.description`, { defaultValue: config.description });

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full font-medium ${config.bg} ${config.text} ${sizeClasses[size]} ${className}`}
      title={`${label}: ${description}`}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{label}</span>
      {showScore && riskScore !== undefined && (
        <span className="opacity-75">({Math.round(riskScore)})</span>
      )}
    </span>
  );
}

/**
 * DriverRiskCard - Larger card for detail views
 */
export function DriverRiskCard({
  riskProfile,
  showDetails = true,
  className = ''
}) {
  const { t } = useTranslation();
  if (!riskProfile) {
    return (
      <div className={`bg-gray-50 rounded-lg p-4 ${className}`}>
        <div className="text-gray-500 text-sm">{t('drivers.risk.unavailable')}</div>
      </div>
    );
  }

  const category = riskProfile.risk_category || getRiskCategory(riskProfile.current_risk_score);
  const config = RISK_CONFIG[category] || RISK_CONFIG.average;
  const Icon = config.icon;
  const score = riskProfile.current_risk_score || 100;
  const code = RISK_CONFIG[category] ? category : 'average';

  return (
    <div className={`${config.bg} ${config.border} border rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-full ${config.bg} flex items-center justify-center`}>
            <Icon className={`w-6 h-6 ${config.text}`} />
          </div>
          <div>
            <div className={`text-lg font-bold ${config.text}`}>
              {t(`drivers.risk.categories.${code}.label`, { defaultValue: config.label })}
            </div>
            <div className="text-sm opacity-75">{t(`drivers.risk.categories.${code}.description`, { defaultValue: config.description })}</div>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-3xl font-bold ${config.text}`}>
            {Math.round(score)}
          </div>
          <div className="text-xs text-gray-500">{t('drivers.risk.score')}</div>
        </div>
      </div>

      {showDetails && (
        <div className="mt-4 pt-4 border-t border-current border-opacity-20 grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-gray-600">{t('drivers.risk.trainingBonus')}</div>
            <div className={`font-medium ${riskProfile.training_bonus < 0 ? 'text-green-600' : 'text-gray-800'}`}>
              {riskProfile.training_bonus > 0 ? '+' : ''}{riskProfile.training_bonus || 0}
            </div>
          </div>
          <div>
            <div className="text-gray-600">{t('drivers.risk.claimsMalus')}</div>
            <div className={`font-medium ${riskProfile.claims_malus > 0 ? 'text-red-600' : 'text-gray-800'}`}>
              {riskProfile.claims_malus > 0 ? '+' : ''}{riskProfile.claims_malus || 0}
            </div>
          </div>
          <div>
            <div className="text-gray-600">{t('drivers.risk.experienceBonus')}</div>
            <div className={`font-medium ${riskProfile.experience_bonus < 0 ? 'text-green-600' : 'text-gray-800'}`}>
              {riskProfile.experience_bonus > 0 ? '+' : ''}{riskProfile.experience_bonus || 0}
            </div>
          </div>
          <div>
            <div className="text-gray-600">{t('drivers.risk.claims12m')}</div>
            <div className="font-medium">
              {riskProfile.claims_last_12_months || 0}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * RiskScoreGauge - Visual gauge for risk score
 */
export function RiskScoreGauge({ score, size = 'md', className = '' }) {
  const category = getRiskCategory(score);
  const config = RISK_CONFIG[category] || RISK_CONFIG.average;

  // Normalize score to percentage (50-150 range to 0-100%)
  const normalizedPercent = Math.max(0, Math.min(100, ((score - 50) / 100) * 100));

  const sizeClasses = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  };

  return (
    <div className={`relative ${className}`}>
      <div className={`w-full ${sizeClasses[size]} bg-gray-200 rounded-full overflow-hidden`}>
        {/* Gradient background showing risk zones */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: 'linear-gradient(to right, #10b981 0%, #22c55e 20%, #eab308 40%, #f97316 60%, #ef4444 80%, #dc2626 100%)'
          }}
        />
        {/* Score indicator */}
        <div
          className={`${sizeClasses[size]} ${config.bg.replace('100', '500')} rounded-full transition-all duration-500`}
          style={{ width: `${normalizedPercent}%` }}
        />
      </div>
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>50</span>
        <span className={`font-medium ${config.text}`}>{Math.round(score)}</span>
        <span>150</span>
      </div>
    </div>
  );
}

export default DriverRiskBadge;
export { getRiskCategory, RISK_CONFIG };
